// ============================================================================
// StorageB5WorksheetPanel — Dediziertes Speicher-Bewertungs-Arbeitsblatt
// ----------------------------------------------------------------------------
// Tiefes Experten-Arbeitsblatt für die Speicher-Wirtschaftlichkeit (B5).
//
// Berechnung INLINE (spiegelt packages/reasoning-core/src/domains/storage*.ts):
//   Annahme: 250 Vollzyklen/Jahr (konservativ)
//   Einsparung = kWh × Zyklen × (Strompreis − Einspeisetarif) [€/kWh]
//   Amortisation = Investment / Einsparung
//   NPV (20 Jahre, 0% Diskont) = Einsparung × 20 − Investment
//
// Sicherheitsinvarianten (strukturell erzwungen):
//   canApprove = false (Literal-Typ)
//   requiresHumanReview = true (Literal-Typ)
//   Status "approved"/"production_ready"/"released" NIEMALS gesetzt
// ============================================================================

import React, { useState, useMemo, useCallback } from "react";
import { eur } from "@/lib/format.js";

// ─── Konstanten (spiegelt reasoning-core/storage-estimator.ts) ──────────────

const ASSUMED_CYCLES_PER_YEAR = 250 as const;
const STORAGE_LIFETIME_YEARS = 20 as const;
const MAX_VIABLE_PAYBACK_YEARS = 20 as const;

// ─── Sicherheitsinvarianten (strukturelle Typen) ────────────────────────────

/** canApprove ist strukturell false — KI gibt Speicher nie frei. */
const CAN_APPROVE = false as const;
/** requiresHumanReview ist strukturell true. */
const REQUIRES_HUMAN_REVIEW = true as const;

// ─── Typen ──────────────────────────────────────────────────────────────────

export interface StorageB5InitialValues {
  speicherKwh?: number | null;
  speicherInvestEur?: number | null;
  strompreisCtProKwh?: number | null;
  einspeisetarifCtProKwh?: number | null;
  pvSystemKwp?: number | null;
  existingPaybackYears?: number | null;
}

export interface WorksheetInputs {
  speicherKwh: number | null;
  speicherInvestEur: number | null;
  strompreisCtProKwh: number | null;
  einspeisetarifCtProKwh: number | null;
  pvSystemKwp: number | null;
  existingPaybackYears: number | null;
}

type WorksheetStatus =
  | "empty"            // Kein Speicher angegeben
  | "incomplete"       // Pflichtfelder fehlen
  | "invalid_spread"   // Einspeisetarif >= Strompreis — kein positiver Spread
  | "evaluated";       // Vollständig bewertet

interface WorksheetResult {
  status: WorksheetStatus;
  canApprove: typeof CAN_APPROVE;
  requiresHumanReview: typeof REQUIRES_HUMAN_REVIEW;
  missingFields: string[];
  assumptions: string[];
  breakdown: {
    annualSavingsEur: number;
    spreadCtPerKwh: number;
    cyclesPerYear: number;
    capacityKwh: number;
  } | null;
  evaluation: {
    storagePaybackYears: number;
    additionalNpvEur: number;
    isViable: boolean;
    verdict: "advice" | "warning" | "critical";
    guardrail: string;
  } | null;
}

// ─── Berechnungslogik (spiegelt reasoning-core, kein Server-Roundtrip) ──────
// Exportiert für Test-Vektoren-Abgleich mit reasoning-core (storage-estimator).

export function computeStorageWorksheet(inputs: WorksheetInputs): WorksheetResult {
  const base: Pick<WorksheetResult, "canApprove" | "requiresHumanReview"> = {
    canApprove: CAN_APPROVE,
    requiresHumanReview: REQUIRES_HUMAN_REVIEW,
  };

  const { speicherKwh, speicherInvestEur, strompreisCtProKwh, einspeisetarifCtProKwh, pvSystemKwp } = inputs;

  // Kein Speicher
  if (speicherKwh == null || speicherKwh <= 0) {
    return { ...base, status: "empty", missingFields: [], assumptions: [], breakdown: null, evaluation: null };
  }

  // Pflichtfelder prüfen
  const missingFields: string[] = [];
  if (speicherInvestEur == null || speicherInvestEur <= 0) missingFields.push("Speicher-Investitionskosten (€)");
  if (strompreisCtProKwh == null || strompreisCtProKwh <= 0) missingFields.push("Strompreis (ct/kWh)");
  if (pvSystemKwp == null || pvSystemKwp <= 0) missingFields.push("PV-Anlagengröße (kWp)");

  if (missingFields.length > 0) {
    return { ...base, status: "incomplete", missingFields, assumptions: [], breakdown: null, evaluation: null };
  }

  // Typerzwingung nach Prüfungen
  const kwh = speicherKwh;
  const invest = speicherInvestEur!;
  const stromEur = strompreisCtProKwh! / 100;
  const einspEur = einspeisetarifCtProKwh != null && einspeisetarifCtProKwh > 0
    ? einspeisetarifCtProKwh / 100
    : 0;
  const spreadEur = stromEur - einspEur;

  // Negativer Spread: Einspeisetarif >= Strompreis
  if (spreadEur <= 0) {
    return {
      ...base,
      status: "invalid_spread",
      missingFields: ["Positiver Einspar-Spread (Strompreis muss größer als Einspeisetarif sein)"],
      assumptions: [],
      breakdown: null,
      evaluation: null,
    };
  }

  const annualSavingsEur = kwh * ASSUMED_CYCLES_PER_YEAR * spreadEur;
  const storagePaybackYears = parseFloat((invest / annualSavingsEur).toFixed(1));
  const additionalNpvEur = parseFloat((annualSavingsEur * STORAGE_LIFETIME_YEARS - invest).toFixed(0));
  // Konservative Wirtschaftlichkeits-Invariante — GRÜN nur wenn ALLE drei gelten:
  //   (1) jährliche Netto-Einsparung > 0
  //   (2) NPV >= 0
  //   (3) Amortisation <= Richtwert
  // Heute folgt (2) mathematisch aus (3) (0 % Diskont, Nutzungsdauer = Richtwert) —
  // die explizite Prüfung schützt davor, dass eine spätere Konstanten-Änderung
  // (Diskontsatz, Lebensdauer, Schwelle) stillschweigend grüne Urteile bei
  // negativem NPV erlaubt.
  const isViable =
    annualSavingsEur > 0 &&
    additionalNpvEur >= 0 &&
    storagePaybackYears <= MAX_VIABLE_PAYBACK_YEARS;
  const verdict: "advice" | "warning" | "critical" = isViable ? "advice" : "warning";

  const assumptions: string[] = [
    `${ASSUMED_CYCLES_PER_YEAR} Vollzyklen/Jahr (konservativ — tatsächlich 200–350 je nach Nutzung)`,
    `Einsparung = ${kwh} kWh × ${ASSUMED_CYCLES_PER_YEAR} Zyklen × ${(spreadEur * 100).toFixed(1)} ct/kWh = ${annualSavingsEur.toFixed(0)} €/Jahr`,
    `NPV-Berechnung: ${STORAGE_LIFETIME_YEARS} Jahre Nutzungsdauer, 0 % Diskontierung (vereinfacht)`,
    ...(einspeisetarifCtProKwh == null || einspeisetarifCtProKwh <= 0
      ? ["Einspeisetarif: 0 ct/kWh angenommen (nicht angegeben) — konservativ"]
      : []),
  ];

  return {
    ...base,
    status: "evaluated",
    missingFields: [],
    assumptions,
    breakdown: {
      annualSavingsEur,
      spreadCtPerKwh: parseFloat((spreadEur * 100).toFixed(2)),
      cyclesPerYear: ASSUMED_CYCLES_PER_YEAR,
      capacityKwh: kwh,
    },
    evaluation: {
      storagePaybackYears,
      additionalNpvEur,
      isViable,
      verdict,
      guardrail:
        "Speicher-Wirtschaftlichkeit hängt vom individuellen Eigenverbrauchsprofil, Energiepreisen und Förderungen ab. Keine Garantie. Abschließende Beurteilung durch Fachkraft erforderlich.",
    },
  };
}

// ─── Hilfs-Hooks ─────────────────────────────────────────────────────────────

function parseNum(v: string): number | null {
  const n = parseFloat(v.replace(",", "."));
  return isFinite(n) && n > 0 ? n : null;
}

// ─── Sub-Komponenten ──────────────────────────────────────────────────────────

function InputRow({
  label,
  unit,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 3 }}>
        {label} <span style={{ fontWeight: 400, color: "#6b7280" }}>({unit})</span>
      </label>
      <input
        type="number"
        min="0"
        step="any"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "6px 8px",
          border: "1px solid #d1d5db",
          borderRadius: 4,
          fontSize: 13,
          boxSizing: "border-box",
          background: "#fff",
        }}
      />
      {hint && <p style={{ margin: "3px 0 0 0", fontSize: 11, color: "#9ca3af", lineHeight: 1.4 }}>{hint}</p>}
    </div>
  );
}

function KpiRow({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: "ok" | "warning" | "critical";
}) {
  const colors = {
    ok: { bg: "#f0fdf4", border: "#86efac", text: "#14532d" },
    warning: { bg: "#fffbeb", border: "#fcd34d", text: "#92400e" },
    critical: { bg: "#fef2f2", border: "#fca5a5", text: "#7f1d1d" },
  };
  const c = highlight ? colors[highlight] : { bg: "#f9fafb", border: "#e5e7eb", text: "#111827" };

  return (
    <div
      style={{
        padding: "8px 12px",
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 5,
        marginBottom: 8,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{label}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: c.text }}>{value}</span>
      </div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────

export interface StorageB5WorksheetPanelProps {
  /** Optionale Anfangswerte aus Projektdaten (KonzeptPanel o. EconomicsTab). */
  initialValues?: StorageB5InitialValues;
  /** Stil-Variante: "card" (in page) oder "inline" (in Panel) */
  variant?: "card" | "inline";
}

function numToStr(v: number | null | undefined): string {
  return v != null && v > 0 ? String(v) : "";
}

export function StorageB5WorksheetPanel({
  initialValues,
  variant = "card",
}: StorageB5WorksheetPanelProps) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [speicherKwh, setSpeicherKwh] = useState(() => numToStr(initialValues?.speicherKwh));
  const [speicherInvestEur, setSpeicherInvestEur] = useState(() => numToStr(initialValues?.speicherInvestEur));
  const [strompreisCtProKwh, setStrompreisCtProKwh] = useState(() => numToStr(initialValues?.strompreisCtProKwh));
  const [einspeisetarifCtProKwh, setEinspeisetarifCtProKwh] = useState(() =>
    numToStr(initialValues?.einspeisetarifCtProKwh),
  );
  const [pvSystemKwp, setPvSystemKwp] = useState(() => numToStr(initialValues?.pvSystemKwp));
  const [existingPaybackYears, setExistingPaybackYears] = useState(() =>
    numToStr(initialValues?.existingPaybackYears),
  );
  const [showAssumptions, setShowAssumptions] = useState(false);

  // ── Berechnung ─────────────────────────────────────────────────────────────
  const result = useMemo((): WorksheetResult => {
    return computeStorageWorksheet({
      speicherKwh: parseNum(speicherKwh),
      speicherInvestEur: parseNum(speicherInvestEur),
      strompreisCtProKwh: parseNum(strompreisCtProKwh),
      einspeisetarifCtProKwh: parseNum(einspeisetarifCtProKwh),
      pvSystemKwp: parseNum(pvSystemKwp),
      existingPaybackYears: parseNum(existingPaybackYears),
    });
  }, [speicherKwh, speicherInvestEur, strompreisCtProKwh, einspeisetarifCtProKwh, pvSystemKwp, existingPaybackYears]);

  const handleReset = useCallback(() => {
    setSpeicherKwh(numToStr(initialValues?.speicherKwh));
    setSpeicherInvestEur(numToStr(initialValues?.speicherInvestEur));
    setStrompreisCtProKwh(numToStr(initialValues?.strompreisCtProKwh));
    setEinspeisetarifCtProKwh(numToStr(initialValues?.einspeisetarifCtProKwh));
    setPvSystemKwp(numToStr(initialValues?.pvSystemKwp));
    setExistingPaybackYears(numToStr(initialValues?.existingPaybackYears));
  }, [initialValues]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const wrapStyle: React.CSSProperties =
    variant === "card"
      ? { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }
      : {};

  return (
    <div style={wrapStyle}>
      {/* Kopfzeile */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: variant === "card" ? "12px 16px" : "8px 0",
          borderBottom: variant === "card" ? "1px solid #e5e7eb" : "none",
          background: variant === "card" ? "#f9fafb" : "transparent",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>🔋</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
              Speicher-Wirtschaftlichkeit
            </div>
            <div style={{ fontSize: 11, color: "#6b7280" }}>B5 — Tiefes Arbeitsblatt</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {/* Sicherheits-Badges — strukturell unveränderbar */}
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              color: "#92400e",
              borderRadius: 3,
              padding: "2px 7px",
            }}
          >
            ENTWURF
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              background: "#f0f9ff",
              border: "1px solid #7dd3fc",
              color: "#0c4a6e",
              borderRadius: 3,
              padding: "2px 7px",
              whiteSpace: "nowrap",
            }}
          >
            Prüfung nötig
          </span>
        </div>
      </div>

      <div style={{ padding: variant === "card" ? 16 : "12px 0 0" }}>
        {/* Sicherheits-Box */}
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #fcd34d",
            borderRadius: 5,
            padding: "8px 12px",
            marginBottom: 14,
            fontSize: 11,
            color: "#92400e",
            lineHeight: 1.5,
          }}
        >
          <strong>⚠ Entwurf — Menschliche Prüfung erforderlich.</strong>{" "}
          Alle Ergebnisse sind Schätzwerte mit erheblicher Bandbreite (±30 %). Die Bewertung kann
          keine persönliche Fachberatung ersetzen. KI-System gibt Speicher strukturell nie frei
          (canApprove = false).
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>
          {/* Linke Spalte: Eingaben */}
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: "0 0 10px 0", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Eingabeparameter
            </h4>

            <InputRow
              label="Speicher-Kapazität"
              unit="kWh"
              value={speicherKwh}
              onChange={setSpeicherKwh}
              placeholder="z.B. 10"
              hint="Nutzbare Nennkapazität des Speichers"
            />
            <InputRow
              label="Speicher-Investition"
              unit="€"
              value={speicherInvestEur}
              onChange={setSpeicherInvestEur}
              placeholder="z.B. 8500"
              hint="Gesamtkosten inkl. Installation (ohne Förderung)"
            />
            <InputRow
              label="Strompreis"
              unit="ct/kWh"
              value={strompreisCtProKwh}
              onChange={setStrompreisCtProKwh}
              placeholder="z.B. 28"
              hint="Aktueller Netz-Bezugspreis inkl. Steuern"
            />
            <InputRow
              label="Einspeisevergütung"
              unit="ct/kWh"
              value={einspeisetarifCtProKwh}
              onChange={setEinspeisetarifCtProKwh}
              placeholder="z.B. 8.24"
              hint="OeMAG-Tarif oder Spotmarkt-Vergütung (optional)"
            />
            <InputRow
              label="PV-Anlagengröße"
              unit="kWp"
              value={pvSystemKwp}
              onChange={setPvSystemKwp}
              placeholder="z.B. 10"
              hint="Nennleistung der PV-Anlage (Kontext)"
            />
            <InputRow
              label="Amortisation PV-Anlage"
              unit="Jahre"
              value={existingPaybackYears}
              onChange={setExistingPaybackYears}
              placeholder="z.B. 9.5"
              hint="Amortisation des Basissystems (optional, Kontext)"
            />

            {initialValues != null && (
              <button
                onClick={handleReset}
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: "#6b7280",
                  background: "none",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  padding: "3px 8px",
                  cursor: "pointer",
                }}
              >
                Auf Projektdaten zurücksetzen
              </button>
            )}
          </div>

          {/* Rechte Spalte: Ergebnisse */}
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: "#374151", margin: "0 0 10px 0", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Bewertungsergebnis
            </h4>

            {result.status === "empty" && (
              <div style={{ color: "#9ca3af", fontSize: 12, padding: "16px 0" }}>
                Speicher-Kapazität eingeben um Bewertung zu starten.
              </div>
            )}

            {result.status === "incomplete" && (
              <div>
                <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>
                  Fehlende Pflichtfelder:
                </p>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: "#dc2626", lineHeight: 1.7 }}>
                  {result.missingFields.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>
            )}

            {result.status === "invalid_spread" && (
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fca5a5",
                  borderRadius: 5,
                  padding: "10px 12px",
                  fontSize: 12,
                  color: "#7f1d1d",
                }}
              >
                <strong>Kein positiver Einspar-Spread.</strong> Der Einspeisetarif ist gleich oder
                höher als der Strompreis — Speicher reduziert die Kosten nicht. Eingaben prüfen.
              </div>
            )}

            {result.status === "evaluated" && result.evaluation && result.breakdown && (
              <div>
                {/* Haupt-KPIs */}
                {/* "≈ " statt "~": die Tilde direkt vor Zahlen wirkt in KPI-Größe
                    wie ein Minuszeichen (E2E-Befund #4 — positive Werte wurden
                    als negativ gelesen). */}
                <KpiRow
                  label="Amortisation Speicher"
                  value={`≈ ${result.evaluation.storagePaybackYears} Jahre`}
                  sub="Bandbreite ±30 % — kein Garant"
                  highlight={
                    result.evaluation.storagePaybackYears > 20
                      ? "warning"
                      : result.evaluation.storagePaybackYears > 15
                        ? "warning"
                        : "ok"
                  }
                />
                <KpiRow
                  label="Zus. Jährl. Einsparung"
                  value={`≈ ${eur(result.breakdown.annualSavingsEur)}/Jahr`}
                  sub={`${result.breakdown.capacityKwh} kWh × ${result.breakdown.cyclesPerYear} Zyklen × ${result.breakdown.spreadCtPerKwh} ct/kWh`}
                />
                <KpiRow
                  label={`Zus. NPV (${STORAGE_LIFETIME_YEARS} Jahre)`}
                  value={`≈ ${eur(result.evaluation.additionalNpvEur)}`}
                  sub="20 Jahre Nutzungsdauer, 0% Diskont (vereinfacht)"
                  highlight={result.evaluation.additionalNpvEur < 0 ? "warning" : "ok"}
                />

                {/* Wirtschaftlichkeits-Urteil */}
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    background: result.evaluation.isViable ? "var(--color-success-tint)" : "var(--color-warning-tint)",
                    border: `1px solid ${result.evaluation.isViable ? "var(--color-success)" : "var(--color-warning)"}`,
                    borderRadius: 5,
                    fontSize: 12,
                    color: result.evaluation.isViable ? "#14532d" : "#92400e",
                  }}
                >
                  <strong>
                    {result.evaluation.isViable
                      ? "✓ Einsparung positiv, NPV ≥ 0, Amortisation ≤ 20 Jahre — wirtschaftlich plausibel (Entwurf)"
                      : "⚠ Schwache Wirtschaftlichkeit — Amortisation über Richtwert oder NPV negativ"}
                  </strong>
                  <p style={{ margin: "4px 0 0 0", fontSize: 11, lineHeight: 1.4 }}>
                    {result.evaluation.isViable
                      ? "Speicher ist unter diesen Annahmen potenziell wirtschaftlich — keine Garantie. Individuelle Prüfung durch Fachkraft zwingend."
                      : "Unter diesen Annahmen rechnet sich der Speicher nicht sicher (Amortisation > 20 Jahre oder NPV < 0). Förderungen oder bessere Preise könnten das Bild ändern — Beurteilung durch Fachkraft."}
                  </p>
                </div>

                {/* Bestehendes System */}
                {result.evaluation.storagePaybackYears && parseNum(existingPaybackYears) != null && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "#6b7280" }}>
                    Kontext: PV-Anlage ≈ {existingPaybackYears} Jahre + Speicher ≈{" "}
                    {result.evaluation.storagePaybackYears} Jahre =&gt; Gesamt-Investitions-Kontext
                    beachten.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Annahmen-Accordion */}
        {result.assumptions.length > 0 && (
          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10 }}>
            <button
              onClick={() => setShowAssumptions((v) => !v)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: 12,
                color: "#6b7280",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span>{showAssumptions ? "▾" : "▸"}</span>
              <span>Annahmen und Berechnungsweg anzeigen</span>
            </button>
            {showAssumptions && (
              <div
                style={{
                  marginTop: 8,
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 5,
                  padding: "10px 12px",
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 6 }}>
                  Angewandte Annahmen (automatisch dokumentiert):
                </div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "#4b5563", lineHeight: 1.7 }}>
                  {result.assumptions.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
                <div style={{ marginTop: 8, fontSize: 11, color: "#9ca3af" }}>
                  Berechnungslogik spiegelt{" "}
                  <code style={{ fontSize: 10, background: "#f3f4f6", padding: "1px 4px", borderRadius: 2 }}>
                    reasoning-core/domains/storage-estimator.ts
                  </code>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guardrail / Schluss-Disclaimer */}
        {result.evaluation && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              background: "#fef3c7",
              border: "1px solid #fcd34d",
              borderRadius: 5,
              fontSize: 11,
              color: "#78350f",
              lineHeight: 1.5,
            }}
          >
            <strong>Endbeurteilung durch Fachkraft erforderlich.</strong>{" "}
            {result.evaluation.guardrail}
          </div>
        )}

        {/* Versteckte Invarianten-Daten (für Tests + Screen-Reader) */}
        <div aria-hidden="true" data-testid="storage-b5-invariants" style={{ display: "none" }}>
          <span data-can-approve={String(CAN_APPROVE)}>canApprove:{String(CAN_APPROVE)}</span>
          <span data-requires-human-review={String(REQUIRES_HUMAN_REVIEW)}>
            requiresHumanReview:{String(REQUIRES_HUMAN_REVIEW)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default StorageB5WorksheetPanel;
